import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layout } from '../components/layout/Layout'
import { adminApiClient } from '../api/client'
import { Image } from '../types/image'
import { Album } from '../types/album'
import { getThumbnailUrl } from '../lib/images'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import {
  AlertTriangle,
  Image as ImageIcon,
  FolderHeart,
  Trash2,
  CheckSquare,
  Square,
  Check,
  X,
  Loader2,
} from 'lucide-react'

interface OrphanListResponse {
  items: Image[]
  nextCursor?: string
}

export const OrphanedImages = () => {
  const queryClient = useQueryClient()

  // Infinite-scroll query for orphaned images
  const {
    data: orphanData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<OrphanListResponse>({
    queryKey: ['admin', 'orphans'],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit: 50 }
      if (pageParam) params.cursor = pageParam as string
      const { data } = await adminApiClient.get('/orphans', { params })
      return data.data as OrphanListResponse
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })

  // Fetch all albums for the assign-to-album picker
  const { data: albumsData } = useQuery<{ items: Album[]; nextCursor?: string }>({
    queryKey: ['albums', 'admin'],
    queryFn: async () => {
      const { data } = await adminApiClient.get('/albums', { params: { limit: 200 } })
      return data.data as { items: Album[]; nextCursor?: string }
    },
  })
  const albums = albumsData?.items

  // Flatten all loaded orphan images from all pages
  const orphanImages = useMemo(() => {
    const seen = new Set<string>()
    return (
      orphanData?.pages.flatMap((page) => page.items).filter((img) => {
        if (seen.has(img.imageId)) return false
        seen.add(img.imageId)
        return true
      }) || []
    )
  }, [orphanData?.pages])

  // IntersectionObserver for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node) return
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '400px' }
      )
      observerRef.current.observe(node)
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Assign-to-album state
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [targetAlbumId, setTargetAlbumId] = useState('')

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'assign'
    open: boolean
  }>({ type: 'delete', open: false })

  const allSelected = orphanImages.length > 0 && selectedIds.size === orphanImages.length

  const batchAssignMutation = useMutation({
    mutationFn: async ({ imageIds, albumId }: { imageIds: string[]; albumId: string }) => {
      const { data } = await adminApiClient.post('/orphans/batch-assign', { imageIds, albumId })
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orphans'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'summary'] })
      queryClient.invalidateQueries({ queryKey: ['images'] })
      setSelectedIds(new Set())
      setShowAssignModal(false)
      setTargetAlbumId('')
    },
  })

  const batchDeleteMutation = useMutation({
    mutationFn: async (imageIds: string[]) => {
      const { data } = await adminApiClient.post('/orphans/batch-delete', { imageIds })
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orphans'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'summary'] })
      queryClient.invalidateQueries({ queryKey: ['images'] })
      setSelectedIds(new Set())
    },
  })

  const toggleSelect = (imageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(imageId)) {
        next.delete(imageId)
      } else {
        next.add(imageId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(orphanImages.map((img) => img.imageId)))
    }
  }

  const handleAssignOpen = () => {
    if (selectedIds.size === 0) return
    setTargetAlbumId(albums && albums.length > 0 ? albums[0].albumId : '')
    setShowAssignModal(true)
  }

  const handleAssignConfirm = () => {
    if (!targetAlbumId || selectedIds.size === 0) return
    batchAssignMutation.mutate({
      imageIds: Array.from(selectedIds),
      albumId: targetAlbumId,
    })
    setConfirmAction({ type: 'assign', open: false })
  }

  const handleDeleteOpen = () => {
    if (selectedIds.size === 0) return
    setConfirmAction({ type: 'delete', open: true })
  }

  const handleDeleteConfirm = () => {
    batchDeleteMutation.mutate(Array.from(selectedIds))
    setConfirmAction({ type: 'delete', open: false })
  }

  // Determine status badge color and text
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'ready':
        return { label: 'Ready', className: 'bg-green-100 text-green-700' }
      case 'processing':
        return { label: 'Processing', className: 'bg-blue-100 text-blue-700' }
      case 'uploading':
        return { label: 'Uploading', className: 'bg-yellow-100 text-yellow-700' }
      case 'error':
        return { label: 'Error', className: 'bg-red-100 text-red-700' }
      default:
        return { label: status, className: 'bg-gray-100 text-gray-700' }
    }
  }

  return (
    <Layout title="Orphaned Photos">
      {/* Empty State */}
      {!isLoading && orphanImages.length === 0 && !hasNextPage ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No orphaned photos</h2>
          <p className="text-gray-500 max-w-md mx-auto">
            All images are properly assigned to an album. Orphaned photos occur when an album is
            deleted but its images remain in the database.
          </p>
        </div>
      ) : null}

      {/* Loading State */}
      {isLoading ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Scanning for orphaned images...</p>
        </div>
      ) : null}

      {/* Error State */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <h3 className="font-semibold text-red-900 mb-1">Failed to load orphaned images</h3>
          <p className="text-red-700 text-sm mb-4">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : null}

      {/* Orphaned Images */}
      {!isLoading && orphanImages.length > 0 ? (
        <>
          {/* Warning Banner */}
          <div className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>
                {orphanImages.length} orphaned photo{orphanImages.length === 1 ? '' : 's'}
                {hasNextPage ? ' (loaded so far)' : ''}
              </strong>{' '}
              — image record(s) whose album no longer exists. Select images below to assign them to
              an album or delete them permanently.
            </div>
          </div>

          {/* Selection Toolbar */}
          <div className="mb-6 flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="w-4 h-4 text-blue-600" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>

            {selectedIds.size > 0 && (
              <>
                <span className="text-sm text-gray-500">{selectedIds.size} selected</span>
                <div className="h-5 w-px bg-gray-200" />
                <button
                  onClick={handleAssignOpen}
                  disabled={batchAssignMutation.isPending}
                  className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {batchAssignMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FolderHeart className="w-4 h-4" />
                  )}
                  Assign to Album
                </button>
                <button
                  onClick={handleDeleteOpen}
                  disabled={batchDeleteMutation.isPending}
                  className="flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {batchDeleteMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete
                </button>
              </>
            )}
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {orphanImages.map((image) => {
              const isSelected = selectedIds.has(image.imageId)
              const statusInfo = getStatusInfo(image.status)

              return (
                <div
                  key={image.imageId}
                  className={`group relative bg-white rounded-xl border overflow-hidden hover:shadow-md transition-all cursor-pointer ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                  onClick={() => toggleSelect(image.imageId)}
                >
                  {/* Checkbox overlay */}
                  <div
                    className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/80 text-transparent group-hover:text-gray-400'
                    }`}
                  >
                    {isSelected ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </div>

                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-100">
                    {getThumbnailUrl(image, 'small') ? (
                      <img
                        src={getThumbnailUrl(image, 'small')}
                        alt={image.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3 space-y-1.5">
                    <p className="text-sm font-medium text-gray-900 truncate" title={image.filename}>
                      {image.filename}
                    </p>
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {image.width}x{image.height}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="flex justify-center items-center py-8">
            {isFetchingNextPage && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading more...</span>
              </div>
            )}
            {!hasNextPage && orphanImages.length > 0 && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Check className="w-4 h-4" />
                All orphaned photos loaded
              </div>
            )}
          </div>

          {/* Assign to Album Modal */}
          {showAssignModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setShowAssignModal(false)}
              />
              <div
                className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="assign-modal-title"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 id="assign-modal-title" className="text-lg font-semibold text-gray-900">
                    Assign {selectedIds.size} photo{selectedIds.size === 1 ? '' : 's'} to album
                  </h3>
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {albums && albums.length > 0 ? (
                  <>
                    <select
                      value={targetAlbumId}
                      onChange={(e) => setTargetAlbumId(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      {albums.map((album) => (
                        <option key={album.albumId} value={album.albumId}>
                          {album.name} ({album.imageCount ?? 0} photos)
                        </option>
                      ))}
                    </select>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={() => setShowAssignModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setConfirmAction({ type: 'assign', open: true })}
                        disabled={!targetAlbumId || batchAssignMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {batchAssignMutation.isPending ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Assigning...
                          </span>
                        ) : (
                          'Assign'
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <FolderHeart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No albums available. Create one first.</p>
                    <button
                      onClick={() => setShowAssignModal(false)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirm Delete Dialog */}
          <ConfirmDialog
            isOpen={confirmAction.open && confirmAction.type === 'delete'}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setConfirmAction({ type: 'delete', open: false })}
            title="Delete orphaned photos"
            message={`Are you sure you want to permanently delete ${selectedIds.size} orphaned photo${selectedIds.size === 1 ? '' : 's'}? This will also remove the original files and thumbnails from storage. This action cannot be undone.`}
            confirmText={`Delete ${selectedIds.size} photo${selectedIds.size === 1 ? '' : 's'}`}
            variant="danger"
          />

          {/* Confirm Assign Dialog */}
          <ConfirmDialog
            isOpen={confirmAction.open && confirmAction.type === 'assign'}
            onConfirm={handleAssignConfirm}
            onCancel={() => setConfirmAction({ type: 'assign', open: false })}
            title="Assign photos to album"
            message={`Assign ${selectedIds.size} orphaned photo${selectedIds.size === 1 ? '' : 's'} to the selected album? They will be removed from this orphaned list and become visible according to the album's visibility settings.`}
            confirmText={`Assign ${selectedIds.size} photo${selectedIds.size === 1 ? '' : 's'}`}
            variant="primary"
          />
        </>
      ) : null}
    </Layout>
  )
}
